import { CompanyProfile } from "../../../types/company-profile.type";
import { BdcShipmentDetail } from "../../../types/bdc.type";
import { BDC_INVOICE_TITLE } from "../constants";
import { baht, pcs, thaiLongDate } from "../format";

const COLS = 7;

/**
 * ใบส่งสินค้า (ร่าง) A4 — every line of the PO, subtotal / VAT / total as the PO states them.
 * Not a tax invoice: P1 issues no number, so the document refers to the 7-11 PO instead.
 *
 * The whole document head sits in <thead> so the browser repeats it on every printed page;
 * the totals are ordinary rows at the end (a <tfoot> would repeat on every page too).
 */
export default function InvoiceA4({
  detail,
  seller,
}: Readonly<{ detail: BdcShipmentDetail; seller: CompanyProfile | null }>) {
  const { header, items } = detail;
  return (
    <div className="bdc-a4">
      <table className="bdc-inv">
        <thead>
          <tr>
            <th colSpan={COLS} className="doc-head">
              <div className="head-row">
                <div>
                  <div className="seller">{seller?.name_th ?? "ผู้ขาย"}</div>
                  {seller?.full_address && <div>{seller.full_address}</div>}
                  {seller?.tax_id && (
                    <div>
                      เลขประจำตัวผู้เสียภาษี {seller.tax_id}
                      {seller.branch_name ? ` (${seller.branch_name})` : ""}
                    </div>
                  )}
                </div>
                <div className="right">
                  <div className="doc-title">{BDC_INVOICE_TITLE}</div>
                  <div>
                    อ้างอิง PO <b>{header.po_number}</b>
                  </div>
                  <div>วันที่ส่ง {thaiLongDate(header.delivery_date)}</div>
                  <div>วันที่สั่ง {thaiLongDate(header.order_date)}</div>
                </div>
              </div>
              <div className="parties">
                <div>
                  <div className="label">ผู้ซื้อ</div>
                  <b>{header.buyer_name ?? "-"}</b>
                  {header.buyer_address && <div>{header.buyer_address}</div>}
                  {/* buyer_tax_id from ORDS is a GS1 location number, not CP All's tax ID — not printed until the owner confirms */}
                </div>
                <div>
                  <div className="label">ส่งที่</div>
                  <b>
                    {header.dc_name} ({header.dc_code})
                  </b>
                  <div>
                    อ้างอิง PO <b>{header.po_number}</b>
                    {header.kind === "A" && header.round != null ? ` · รอบ ${header.round}` : ""}
                  </div>
                </div>
              </div>
            </th>
          </tr>
          <tr className="cols">
            <th>#</th>
            <th>Barcode</th>
            <th>รหัส 7-11</th>
            <th>รายการ</th>
            <th>จำนวน</th>
            <th>ราคา/หน่วย</th>
            <th>จำนวนเงิน</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, i) => (
            <tr key={item.barcode}>
              <td className="c">{i + 1}</td>
              <td className="mono">{item.barcode}</td>
              <td className="mono">{item.item_code}</td>
              <td>
                {item.name} {item.size ?? ""}
              </td>
              <td className="n">{pcs(item.qty)}</td>
              <td className="n">{baht(item.unit_price)}</td>
              <td className="n">{baht(item.amount)}</td>
            </tr>
          ))}
          <tr className="sum">
            <td colSpan={4}>
              {items.length} รายการ · {pcs(header.pieces)} ชิ้น
            </td>
            <td colSpan={2} className="n">
              รวมเป็นเงิน
            </td>
            <td className="n">{baht(header.subtotal)}</td>
          </tr>
          <tr className="sum">
            <td colSpan={6} className="n">
              ภาษีมูลค่าเพิ่ม 7%
            </td>
            <td className="n">{baht(header.vat)}</td>
          </tr>
          <tr className="sum">
            <td colSpan={6} className="n">
              <b>รวมทั้งสิ้น</b>
            </td>
            <td className="n">
              <b>{baht(header.total)}</b>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}

/** Styles for the A4 sheet, shared by the screen preview and the print portal. */
export const INVOICE_SX = {
  "& .bdc-a4": {
    width: 794,
    maxWidth: "100%",
    minHeight: 1123,
    margin: "0 auto",
    background: "#fff",
    border: "1px solid #E3E5EA",
    boxShadow: "0 2px 10px rgba(0,0,0,.06)",
    padding: "40px 44px",
    fontSize: "12.5px",
    color: "#1F2329",
  },
  "& table.bdc-inv": { width: "100%", borderCollapse: "collapse" },
  "& .bdc-inv th.doc-head": {
    border: 0,
    background: "#fff",
    padding: "0 0 12px",
    textAlign: "left",
    fontWeight: 400,
  },
  "& .bdc-inv .head-row": { display: "flex", justifyContent: "space-between", gap: "16px" },
  "& .bdc-inv .head-row .right": { textAlign: "right" },
  "& .bdc-inv .seller": { fontSize: "16px", fontWeight: 600 },
  "& .bdc-inv .doc-title": { fontSize: "16px", fontWeight: 600, marginBottom: "4px" },
  "& .bdc-inv .parties": {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "16px",
    marginTop: "12px",
    border: "1px solid #BFC3CB",
    padding: "8px 10px",
  },
  "& .bdc-inv .label": { color: "#6B7080", fontSize: "11.5px" },
  "& .bdc-inv tr.cols th": {
    border: "1px solid #BFC3CB",
    background: "#F4F5F7",
    fontWeight: 500,
    padding: "5px 6px",
  },
  "& .bdc-inv td": { border: "1px solid #BFC3CB", padding: "5px 6px" },
  "& .bdc-inv .n": { textAlign: "right", fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" },
  "& .bdc-inv .c": { textAlign: "center" },
  "& .bdc-inv tr": { breakInside: "avoid" },
  "& .bdc-inv .mono": { fontFamily: "ui-monospace, Consolas, monospace", fontSize: "11.5px" },
} as const;
